import { useIncidentStore, type IncidentView } from '@/incidents/incident-store';
import { getTracker } from '@/telemetry/tracker';

/**
 * Status-page incident banner. Mirrors the legacy `ey-status-toaster` shown by
 * `LayoutComponent` (bottom-left, dismissible, links to the status page). It is
 * driven by the incident store, which is fed by the SignalR
 * `portalincidentstatusmethod` hub + an initial fetch (see incidents-bridge).
 *
 * Hidden when there are no incidents or the user already dismissed today's set.
 */

/** Status page link — reuses the same statuspage host as the Help panel. */
const STATUS_PAGE_URL = import.meta.env.VITE_PLATFORM_STATUS_URL;

/** Newest incident by update/create time (legacy `getLatestIncident`). */
function latestIncident(incidents: IncidentView[]): IncidentView | undefined {
  return [...incidents].sort((a, b) => {
    const aTime = new Date(a.updatedAt || a.createdAt || 0).getTime();
    const bTime = new Date(b.updatedAt || b.createdAt || 0).getTime();
    return bTime - aTime;
  })[0];
}

/** Human relative time (legacy `getRelativeTimeFromNow`). */
function relativeTime(dateString?: string): string {
  if (!dateString) return 'just now';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'just now';

  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/** Known status-page statuses (legacy `mapIncidentStatus`). */
const KNOWN_STATUSES = new Set(['investigating', 'identified', 'monitoring', 'resolved']);

function statusLabel(status?: string): string | undefined {
  const normalized = (status || '').toLowerCase();
  if (!KNOWN_STATUSES.has(normalized)) return undefined;
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export function IncidentBanner() {
  const incidents = useIncidentStore((s) => s.incidents);
  const dismissed = useIncidentStore((s) => s.dismissed);
  const dismiss = useIncidentStore((s) => s.dismiss);

  if (dismissed || incidents.length === 0) return null;

  const latest = latestIncident(incidents);
  if (!latest) return null;

  const moreCount = Math.max(incidents.length - 1, 0);
  const subtitle =
    moreCount > 0
      ? `+${moreCount} more incident${moreCount > 1 ? 's' : ''}`
      : `Last updated ${relativeTime(latest.updatedAt || latest.createdAt)}`;
  const status = statusLabel(latest.status);

  const onClose = () => {
    getTracker().event('incident_banner_dismissed', { count: incidents.length });
    dismiss();
  };

  return (
    <aside className="incident-banner" role="status" aria-live="polite">
      <div className="incident-banner__body">
        <div className="incident-banner__head">
          {status ? <span className="incident-banner__status">{status}</span> : null}
          <strong className="incident-banner__title">{latest.name || 'Incident update'}</strong>
        </div>
        <p className="incident-banner__subtitle">{subtitle}</p>
        {STATUS_PAGE_URL ? (
          <a
            className="incident-banner__link"
            href={STATUS_PAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => getTracker().event('incident_banner_link_clicked')}
          >
            View latest updates
          </a>
        ) : null}
      </div>
      <button
        type="button"
        className="incident-banner__close"
        aria-label="Dismiss incident notification"
        onClick={onClose}
      >
        &times;
      </button>
    </aside>
  );
}
